import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Crown, Calendar, Loader2 } from 'lucide-react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store'
import { setSingleRoom } from '../store/roomsSlice'
import roomsService from '../services/roomsService'
import { useTranslation } from '../hooks/use-i18n'
import futureChampionService from '../services/futureChampionService'
import { FutureChampionEvent, FutureChampionPrediction } from '../types/futureChampion'
import FutureChampionExistedModal from '../components/FutureChampionExistedModal'
import { toast } from 'react-hot-toast'
import { getWorldCup2026TeamFlag } from '../constants/worldCup2026TeamFlags'

const FutureChampionPlay: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { user } = useSelector((state: RootState) => state.auth)
  const { singleRoom } = useSelector((state: RootState) => state.rooms)

  const [events, setEvents] = useState<FutureChampionEvent[]>([])
  const [predictionsMap, setPredictionsMap] = useState<Record<string, FutureChampionPrediction>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showExistedModal, setShowExistedModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [query, setQuery] = useState('')

  const toGameType = (gt: string | undefined): 'football' | 'formula1' =>
    gt === 'formula1' ? 'formula1' : 'football'
  const [gameType, setGameType] = useState<'football' | 'formula1'>(() =>
    toGameType(singleRoom?.gameType)
  )
  const playerId = user?._id ?? (user as any)?.id ?? ''

  // Ensure we have room (and gameType) when opening play page directly
  useEffect(() => {
    if (!roomId) return
    if (singleRoom && (singleRoom.id === roomId || (singleRoom as any)._id === roomId)) {
      setGameType(toGameType(singleRoom.gameType))
      return
    }
    let cancelled = false
    roomsService.getRoom(roomId).then((room) => {
      if (cancelled || !room) return
      dispatch(setSingleRoom(room))
      setGameType(toGameType(room.gameType))
    }).catch(() => { if (!cancelled) setGameType('football') })
    return () => { cancelled = true }
  }, [roomId, singleRoom, dispatch])

  const eventOrder = (name: string) => {
    if (!name) return 99
    const n = name.toLowerCase()
    if (n.includes('start')) return 1
    if (n.includes('second')) return 2
    if (n.includes('third')) return 3
    return 99
  }

  const orderedEvents = useMemo(() => {
    const n = gameType === 'football' ? 2 : 3
    return [...events].sort((a, b) => eventOrder(a.name) - eventOrder(b.name)).slice(0, n)
  }, [events, gameType])

  const currentEvent = orderedEvents[selectedIndex] ?? null
  const currentPrediction = currentEvent ? predictionsMap[currentEvent.id ?? (currentEvent as any)._id] : null

  const filteredChoices = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = currentEvent?.choices || []
    if (!q) return list
    return list.filter((c) => normalizeChoiceDisplay(c).toLowerCase().includes(q))
  }, [currentEvent, query])

  useEffect(() => { setQuery('') }, [selectedIndex])

  useEffect(() => {
    if (!roomId || !playerId) return
    let cancelled = false
    setLoading(true)
    Promise.all([
      futureChampionService.getEvents({ gameType, isActive: true }),
      futureChampionService.getPlayerPredictions(roomId, playerId),
    ])
      .then(([eventsList, preds]) => {
        if (cancelled) return
        setEvents(Array.isArray(eventsList) ? eventsList : [])
        const map: Record<string, FutureChampionPrediction> = {}
        ;(Array.isArray(preds) ? preds : []).forEach((p) => {
          const eid = p.eventId ?? (p.event as any)?.id
          if (eid) map[eid] = p
        })
        setPredictionsMap(map)
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([])
          setPredictionsMap({})
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [roomId, playerId, gameType])

  const isEventOpen = (event: FutureChampionEvent) => {
    if (!event.isActive) return false
    return new Date() < new Date(event.eventDate)
  }

  const handleChoiceClick = async (choice: string) => {
    if (!roomId || !currentEvent || submitting) return
    const eid = currentEvent.id ?? (currentEvent as any)._id
    const existing = predictionsMap[eid]

    if (existing && !isEditMode) {
      setShowExistedModal(true)
      return
    }

    try {
      setSubmitting(true)
      const prediction = await futureChampionService.createPrediction({
        roomId,
        eventId: eid,
        predictedChampion: choice,
      })
      setPredictionsMap((prev) => ({ ...prev, [eid]: prediction }))
      toast.success(t('futureChampion.saved') || 'Prediction saved')
      setSelectedIndex((i) => Math.min(orderedEvents.length - 1, i + 1))
      setShowExistedModal(false)
      setIsEditMode(false)
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save')
    } finally {
      setSubmitting(false)
    }
  }

  const handleKeep = () => {
    setShowExistedModal(false)
    setSelectedIndex((i) => Math.min(orderedEvents.length - 1, i + 1))
  }

  const handleChange = () => {
    setShowExistedModal(false)
    setIsEditMode(true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Normalize choice text (strip PostgreSQL/JSON artifacts: { } " and trim)
  const normalizeChoiceDisplay = (raw: string): string => {
    if (typeof raw !== 'string') return String(raw)
    return raw.replace(/^[\s"{]+|[\s"}]+$/g, '').trim()
  }

  const renderChampionChoice = (rawChoice: string) => {
    const choice = normalizeChoiceDisplay(rawChoice)
    const flag = getWorldCup2026TeamFlag(choice)
    if (!flag) return <>{choice}</>
    return (
      <span className="inline-flex items-center justify-center gap-1.5">
        <span style={{ fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, emoji' }}>
          {flag}
        </span>
        <span>{choice}</span>
      </span>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    )
  }

  if (orderedEvents.length === 0) {
    return (
      <div className="min-h-screen p-4 flex flex-col items-center justify-center">
        <Crown className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t('futureChampion.noEvents') || 'No future champion events'}
        </h2>
        <Link to={`/room/${roomId}`} className="text-primary font-medium">
          {t('predictions.backToRoom')}
        </Link>
      </div>
    )
  }

  const allDone = selectedIndex >= orderedEvents.length

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        <Link
          to={`/room/${roomId}`}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 sm:mb-6 text-small sm:text-body"
        >
          <ArrowLeft className="w-4 h-4 shrink-0" />
          {t('predictions.backToRoom')}
        </Link>

        <div className="blue-sky-card rounded-2xl p-6 sm:p-8 text-center">
          {allDone ? (
            <>
              <div className="text-6xl mb-6">🏆</div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                {t('futureChampion.allDone') || 'All future champion predictions done'}
              </h2>
              <button
                onClick={() => navigate(`/room/${roomId}`)}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold"
              >
                {t('predictions.backToRoomButton')}
              </button>
            </>
          ) : currentEvent ? (
            <>
              <div className="flex items-center justify-between w-full mb-6">
                <div className="flex gap-1 sm:gap-1.5">
                  {orderedEvents.map((_, i) => (
                    <div
                      key={i}
                      className={`h-2 w-4 sm:w-5 rounded-sm flex-shrink-0 ${
                        i <= selectedIndex ? 'bg-primary' : 'bg-white/20'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-white/80 font-semibold text-small sm:text-body ml-2">
                  {selectedIndex + 1}/{orderedEvents.length}
                </span>
              </div>

              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-3">
                <Crown className="w-7 h-7 text-yellow-400" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">
                {t('futureChampion.title') || 'Future Champion'}
              </h1>
              <h2 className="text-lg font-semibold text-white/90 mb-2">{currentEvent.name}</h2>
              <div className="flex items-center justify-center gap-2 text-white/80 text-sm mb-6">
                <Calendar className="w-4 h-4" />
                <span>Deadline: {formatDate(currentEvent.eventDate)}</span>
              </div>

              {!isEventOpen(currentEvent) ? (
                <p className="text-white/80">
                  {t('futureChampion.closed') || 'Predictions closed for this event.'}
                </p>
              ) : (
                <>
                  <p className="text-white/90 text-sm sm:text-body mb-3">
                    {t('futureChampion.selectChampion') || 'Select your predicted champion:'}
                  </p>
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('futureChampion.searchPlaceholder')}
                    className="w-full mb-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-primary"
                  />
                  <p className="text-white/60 text-xs mb-3 text-left">{t('futureChampion.searchHint')}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {filteredChoices.map((choice) => (
                      <button
                        key={String(choice)}
                        onClick={() => handleChoiceClick(choice)}
                        disabled={submitting}
                        className="px-4 py-3 sm:py-4 bg-primary hover:opacity-90 disabled:opacity-50 text-primary-foreground rounded-xl font-semibold text-sm sm:text-body transition-opacity"
                      >
                        {renderChampionChoice(choice)}
                      </button>
                    ))}
                    {filteredChoices.length === 0 && (
                      <p className="col-span-2 sm:col-span-3 text-white/70 text-sm text-center py-4">
                        {t('topScorer.noResults')}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="flex items-center justify-center gap-4 mt-6 sm:mt-8">
                <button
                  type="button"
                  onClick={() => setSelectedIndex((i) => Math.max(0, i - 1))}
                  disabled={selectedIndex === 0}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedIndex((i) => Math.min(orderedEvents.length - 1, i + 1))}
                  disabled={selectedIndex >= orderedEvents.length - 1}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {showExistedModal && currentPrediction && (
                <FutureChampionExistedModal
                  eventName={currentEvent.name}
                  currentChoice={currentPrediction.predictedChampion}
                  onKeepClick={handleKeep}
                  onChangeClick={handleChange}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default FutureChampionPlay
